import Link from 'next/link';

import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { Event } from '~/ui/components/analytics';
import { Button } from '~/ui/components/button';
import { Column, type ColumnProps, Grid } from '~/ui/components/grid';
import { Logo } from '~/ui/components/logo';
import { Image } from '~/ui/modules/image';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getInternalLinkFromFragment } from '~/ui/systems/internalLink';
import { formatDate } from '~/ui/utils/formatDate';

import styles from './call-for-concepts.module.scss';
import { type CallForConcepts_CallForConceptFragment } from './query';

export interface CallForConceptsProps extends Omit<ColumnProps, 'span' | 'children'> {
  data: Masked<typeof CallForConcepts_CallForConceptFragment>;
  isFullWidth?: boolean;
}

export function CallForConcepts({ data, isFullWidth, className, ...props }: CallForConceptsProps) {
  const { type, title, grantMakingArea, article, registrationDeadlineDate } =
    unmask<typeof CallForConcepts_CallForConceptFragment>(data);
  const ctaUrl = article ? getInternalLinkFromFragment(article) : '';

  return (
    <Column
      span={
        isFullWidth
          ? { [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 7, [Breakpoint.DESKTOP]: 7 }
          : { [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 3 }
      }
      offsetLeft={isFullWidth ? { [Breakpoint.DESKTOP]: 1 } : undefined}
      className={cx(styles.card, className, {
        [styles.fullWidth]: isFullWidth,
      })}
      {...props}
    >
      <Grid className={styles.logoContainer}>
        <Column
          span={{
            [Breakpoint.MOBILE]: 1,
            [Breakpoint.TABLET]: 1,
            [Breakpoint.DESKTOP]: 1,
          }}
          offsetLeft={
            isFullWidth
              ? { [Breakpoint.MOBILE]: 2, [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 3 }
              : { [Breakpoint.MOBILE]: 2, [Breakpoint.TABLET]: 1, [Breakpoint.DESKTOP]: 1 }
          }
        >
          {/* TODO: is title a required field in the CMS? */}
          <Logo randomSeed={title ?? ''} className={styles.logo} />
        </Column>
      </Grid>

      <Grid>
        {grantMakingArea ? (
          <Column className={styles.top}>
            {grantMakingArea.image ? (
              <Image image={grantMakingArea.image} className={styles.image} />
            ) : null}

            <div className={cx(styles.grantmakingArea, typographyStyles.bodySmall)}>
              {type}
              <br />
              {grantMakingArea.name}
            </div>
          </Column>
        ) : null}

        <Column
          span={isFullWidth ? { [Breakpoint.TABLET]: 4, [Breakpoint.DESKTOP]: 4 } : undefined}
          className={cx(typographyStyles.modulesTitle, styles.title)}
        >
          {title}
        </Column>

        <Column
          span={isFullWidth ? { [Breakpoint.TABLET]: 3, [Breakpoint.DESKTOP]: 2 } : undefined}
          offsetLeft={isFullWidth ? { [Breakpoint.TABLET]: 0, [Breakpoint.DESKTOP]: 1 } : undefined}
          className={styles.action}
        >
          <div className={cx(styles.registrationDeadline, typographyStyles.caption)}>
            {registrationDeadlineDate ? (
              <>
                Registration deadline
                <br />
                {formatDate(registrationDeadlineDate, {
                  formatOptions: {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  },
                })}
              </>
            ) : (
              'Applications are accepted on a rolling basis'
            )}
          </div>
          <Button
            render={<Link href={ctaUrl} />}
            event={{
              name: Event.CALL_FOR_CONCEPTS_CLICK,
              data: {
                callForConceptsCTA: 'See details',
                callForConceptsLink: ctaUrl,
              },
            }}
            className={styles.button}
          >
            See details
          </Button>
        </Column>
      </Grid>
    </Column>
  );
}
